import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Button, ButtonCondensed, InputText } from '@companycam/slab-web';
import styled from 'styled-components';

import { translate } from 'translations/i18n';
import steven, { ELEMENT_TYPES } from 'helpers/steven';

import { PageToolbarProps } from './types';
import { ToolbarIconButton } from './ToolbarIconButton';

const ToolbarLinkDialogWrapper = styled.dialog`
  width: 400px;
  padding: 0;
  max-height: 500px;
  flex-direction: column;
  align-items: flex-start;
  border: none;
  border-radius: var(--cc_size_border_radius_l);
  background: var(--cc_color_background_1);

  /* Depth/2 */
  box-shadow: 0px var(--cc_size_depth_2_y) var(--cc_size_depth_2_blur) 0px
    var(--cc_color_depth_2);
`;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--cc_size_spacing_s);
  align-self: stretch;
`;

const InputWrapper = styled.div`
  display: flex;
  padding: var(--cc_size_spacing_m);
  flex-direction: column;
  gap: var(--cc_size_spacing_l);
  align-self: stretch;
`;

const DialogFooter = styled.div`
  display: flex;
  padding: var(--cc_size_spacing_m);
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  background: var(--cc_color_background_2);
`;

const FooterButtons = styled.div`
  display: flex;
  gap: var(--cc_size_spacing_m);
`;

export type LinkButtonProps = {
  editor: PageToolbarProps['editor'];
};

const LinkButton = ({ editor }: LinkButtonProps) => {
  const [activeLinkText, setActiveLinkText] = useState('');
  const [activeLinkUrl, setActiveLinkUrl] = useState('');

  const hasActiveLink = activeLinkText && activeLinkUrl;
  const isRemoveEnabled = activeLinkUrl !== undefined && activeLinkUrl !== '';

  const linkDialogRef = useRef<HTMLDialogElement>();

  const setLink = useCallback(
    (url: string, text: string) => {
      // empty
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();

        return;
      }

      // Format URL if it doesn't start with a protocol
      const formattedUrl = url.match(/^https?:\/\//) ? url : `https://${url}`;

      // insert/update link
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: formattedUrl })
        .command(({ tr: transaction }) => {
          transaction.insertText(text);
          return true;
        })
        .toggleMark('link')
        .run();

      setActiveLinkText('');
      setActiveLinkUrl('');
    },
    [editor],
  );

  const openLinkDialog = () => {
    const { from, to, empty } = editor.state.selection;
    const selectedText = empty
      ? ''
      : editor.state.doc.textBetween(from, to, '');
    setActiveLinkText(selectedText);
    setActiveLinkUrl(editor.getAttributes('link').href || '');
    linkDialogRef.current?.showModal();
  };

  const closeLinkDialog = () => {
    linkDialogRef.current?.close();
    setActiveLinkText('');
    setActiveLinkUrl('');
  };

  const handleLinkSubmit = () => {
    // need a copy of this url for the steven event before setLink resets it
    const newLinkUrl = activeLinkUrl;

    setLink(activeLinkUrl, activeLinkText);
    steven.elementClicked({
      id: 'project_page_insert_link',
      content: newLinkUrl,
      type: ELEMENT_TYPES.BUTTON,
      action: 'project_page_insert_link_clicked',
    });
    closeLinkDialog();
  };

  const handleRemoveLink = () => {
    setLink('', activeLinkUrl);
    closeLinkDialog();
  };

  useEffect(() => {
    const closeDialogOnClickOutisde = (event: MouseEvent) => {
      if (event.target !== linkDialogRef.current) return;
      closeLinkDialog();
    };

    document.addEventListener('click', closeDialogOnClickOutisde);

    return () => {
      document.removeEventListener('click', closeDialogOnClickOutisde);
    };
  }, []);

  return (
    <>
      <ToolbarIconButton
        icon="link-variant"
        accessibilityLabel=""
        onClick={openLinkDialog}
        data-testid="project-page-toolbar__link-button"
      />
      <ToolbarLinkDialogWrapper ref={linkDialogRef}>
        <DialogContent>
          <InputWrapper>
            <InputText
              id="link-dialog-text"
              data-testid="project-page__link-dialog-text"
              label={translate(`pages.linkDialog.text`)}
              onChange={(e) => setActiveLinkText(e.target.value)}
              value={activeLinkText}
            />
            <InputText
              id="link-dialog-url"
              data-testid="project-page__link-dialog-url"
              label={translate(`pages.linkDialog.link`)}
              onChange={(e) => setActiveLinkUrl(e.target.value)}
              value={activeLinkUrl}
            />
          </InputWrapper>
          <DialogFooter>
            <ButtonCondensed
              onClick={handleRemoveLink}
              icon={{
                name: 'link-variant',
                position: 'before',
              }}
              disabled={!isRemoveEnabled}
            >
              {translate(`pages.linkDialog.removeLink`)}
            </ButtonCondensed>
            <FooterButtons>
              <Button onClick={closeLinkDialog}>
                {translate(`common.cancel`)}
              </Button>
              <Button
                color="primary"
                onClick={handleLinkSubmit}
                disabled={!hasActiveLink}
              >
                {translate(`common.done`)}
              </Button>
            </FooterButtons>
          </DialogFooter>
        </DialogContent>
      </ToolbarLinkDialogWrapper>
    </>
  );
};

export default LinkButton;
